/**
 * Response Archive Hook
 * Provides easy access to archived, normalized response data
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { normalizeResponses, NormalizedResponseSet } from '@/services/responseNormalizationService';
import { getCurrentQuestionVersion, getCurrentScoringVersion, detectQuestionVersion } from '@/data/questionRegistry';
import { logger } from '@/utils/structuredLogging';

interface ArchivedResponse {
  id: string;
  assessment_id: string;
  user_id: string;
  test_type: string;
  test_version: string;
  questions_version: string;
  scoring_version: string;
  responses: Record<string, number>;
  response_count: number;
  completion_percentage: number;
  completed_at: string;
  created_at: string;
}

/**
 * Archive assessment responses to response_archive table
 * Supports both authenticated users (userId) and anonymous users (sessionToken)
 */
export async function archiveAssessmentResponses(params: {
  userId?: string;
  sessionToken?: string;
  assessmentId?: string;
  responses: number[] | Record<string, number>;
  testType: string;
  completedAt?: string;
}): Promise<void> {
  const { userId, sessionToken, assessmentId, responses, testType, completedAt } = params;
  
  // Must have either userId or sessionToken
  if (!userId && !sessionToken) {
    logger.warn('Cannot archive: missing both userId and sessionToken', {
      component: 'useResponseArchive',
      action: 'archive',
    });
    return;
  }
  
  const normalized = normalizeResponses(responses, testType);
  if (!normalized) {
    logger.warn('Could not normalize responses for archiving', {
      component: 'useResponseArchive',
      action: 'archive',
    });
    return;
  }

  const questionVersion = detectQuestionVersion(responses, testType);
  
  const archiveData: Record<string, any> = {
    test_type: testType,
    test_version: normalized.metadata.testVersion,
    questions_version: questionVersion?.version || normalized.metadata.questionsVersion,
    scoring_version: getCurrentScoringVersion(testType),
    responses: normalized.responseMap,
    response_count: normalized.metadata.answeredQuestions,
    completion_percentage: normalized.metadata.completionPercentage,
    completed_at: completedAt || new Date().toISOString(),
  };

  // Add user identifier
  if (userId) {
    archiveData.user_id = userId;
  }
  if (sessionToken) {
    archiveData.session_token = sessionToken;
  }
  if (assessmentId) {
    archiveData.assessment_id = assessmentId;
  }

  // Use insert or upsert based on whether we have an assessment_id
  const query = assessmentId 
    ? supabase.from('response_archive').upsert(archiveData as any, { onConflict: 'assessment_id' })
    : supabase.from('response_archive').insert(archiveData as any);

  const { error } = await query;

  if (error) {
    logger.error('Failed to archive responses', {
      component: 'useResponseArchive',
      action: 'archive',
    }, error);
  } else {
    logger.info('Responses archived successfully', {
      component: 'useResponseArchive',
      action: 'archive',
      metadata: { assessmentId, sessionToken, responseCount: normalized.metadata.answeredQuestions },
    });
  }
}

/**
 * Hook to access user's archived responses
 */
export function useUserResponseArchive() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['response-archive', user?.id],
    queryFn: async () => {
      if (!user) return [];

      const { data, error } = await supabase
        .from('response_archive')
        .select('*')
        .eq('user_id', user.id)
        .order('completed_at', { ascending: false });

      if (error) throw error;
      return data as ArchivedResponse[];
    },
    enabled: !!user,
  });
}

/**
 * Hook to get normalized responses for a specific assessment
 */
export function useNormalizedResponses(assessmentId: string | null) {
  return useQuery({
    queryKey: ['normalized-responses', assessmentId],
    queryFn: async () => {
      if (!assessmentId) return null;

      // First check archive
      const { data: archived } = await supabase
        .from('response_archive')
        .select('*')
        .eq('assessment_id', assessmentId)
        .single();

      if (archived) {
        return normalizeResponses(
          archived.responses as unknown as Record<string, number>,
          archived.test_type
        );
      }

      // Fall back to assessment table
      const { data: assessment } = await supabase
        .from('assessments')
        .select('responses, variant')
        .eq('id', assessmentId)
        .single();

      if (assessment?.responses) {
        const testType = assessment.variant === 'integral' ? 'integral' : 'tps';
        return normalizeResponses(
          assessment.responses as unknown as number[] | Record<string, number>,
          testType
        );
      }

      return null;
    },
    enabled: !!assessmentId,
  });
}

/**
 * Hook to get all responses for a specific question key across assessments
 */
export function useQuestionHistory(questionKey: string) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['question-history', user?.id, questionKey],
    queryFn: async () => {
      if (!user) return [];

      const { data } = await supabase
        .from('response_archive')
        .select('assessment_id, responses, completed_at')
        .eq('user_id', user.id)
        .order('completed_at', { ascending: false });

      if (!data) return [];

      return data
        .filter(row => row.responses && questionKey in (row.responses as Record<string, number>))
        .map(row => ({
          assessmentId: row.assessment_id,
          value: (row.responses as Record<string, number>)[questionKey],
          date: row.completed_at,
        }));
    },
    enabled: !!user && !!questionKey,
  });
}

/**
 * Get response statistics for analytics
 */
export function useResponseStats() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['response-stats', user?.id],
    queryFn: async () => {
      if (!user) return null;

      const { data } = await supabase
        .from('response_archive')
        .select('test_type, test_version, response_count, completion_percentage, completed_at')
        .eq('user_id', user.id);

      if (!data || data.length === 0) return null;

      const stats = {
        totalAssessments: data.length,
        byTestType: {} as Record<string, number>,
        avgCompletion: 0,
        totalResponses: 0,
      };

      data.forEach(row => {
        stats.byTestType[row.test_type] = (stats.byTestType[row.test_type] || 0) + 1;
        stats.avgCompletion += row.completion_percentage || 0;
        stats.totalResponses += row.response_count || 0;
      });

      stats.avgCompletion = +(stats.avgCompletion / data.length).toFixed(1);

      return stats;
    },
    enabled: !!user,
  });
}

/**
 * Mutation to manually archive existing assessments (for backfill)
 */
export function useArchiveAssessment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      userId: string;
      assessmentId: string;
      responses: number[] | Record<string, number>;
      testType: string;
      completedAt?: string;
    }) => {
      await archiveAssessmentResponses(params);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['response-archive'] });
    },
  });
}
